import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router, Params } from '@angular/router';
import { UntypedFormControl, UntypedFormGroup, Validators, UntypedFormBuilder, UntypedFormArray } from '@angular/forms';
import { Location } from '@angular/common';

import * as _ from 'lodash';

import { PartsPluginService } from './parts-plugin.service';
import { BaseModalPageComponent } from '../../base-modal.component';

@Component({
  templateUrl: './parts-add.component.html',
  standalone: false
})
export class PartsAddPageComponent extends BaseModalPageComponent implements OnInit {
  inspectionId: string;
  pointId: string;
  pluginId: string;

  // This plugin has 3 modes :
  // partId is null, equipmentId is set => Add part
  // partId is set, equipmentId is null => Edit part
  // both null => Import part
  partId: string;
  equipmentId: string;

  partsPointId: string;
  parts: any;
  part: any;
  importedParts: any;
  partsForm: UntypedFormGroup;
  public processing = false;
  private isImport = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private service: PartsPluginService,
    location: Location,
    private cdRef: ChangeDetectorRef,
    private fb: UntypedFormBuilder
  ) {
    super(location);
  }

  initRoute(): void {
    this.activatedRoute.params.subscribe((params: Params) => {
      const id = params['inspectionId'];
      if (id == null) this.goToBack();

      this.inspectionId = id;

      const pointId = params['pointId'];
      if (pointId == null) {
        this.router.navigate([this.inspectionsRoute, this.inspectionId]);
      }

      this.pointId = pointId;

      const pluginId = params['pluginId'];
      if (pluginId == null) {
        this.goToBack();
      }

      this.pluginId = pluginId;

      this.partId = params['partId'];
      this.equipmentId = params['equipmentId'];

      this.initData();
    });
  }

  cancel() {
    this.goToBack();
  }

  ngOnInit() {
    this.initRoute();
  }

  initData() {
    this.isImport = this.router.url.includes('import');
    if (this.isImport) {
      this.service.getImportedParts().subscribe((data) => {
        this.importedParts = data;
        this.partsForm = this.fb.group({
          parts: this.fb.array(this.setImportedParts())
        });
      });
    } else if (this.partId) {
      this.service.getPlugin(this.inspectionId, this.pluginId).subscribe((data) => {
        const plugin = data;
        if (plugin.config.Parts) {
          let part = plugin.config.Parts.find(
            (p) => p.PointId === this.pointId && `${p.Id}-${p.Page}-${p.Position}-${p.Name}` === this.partId
          );
          part = part ? part : plugin.config.Basket.PartsView.find((p) => `${p.Parts}-${p.Page}-${p.Position}-${p.Name}` === this.partId);
          if (part) {
            this.part = {
              Id: part.Id ? part.Id : part.Parts,
              Quantity: part.Id ? part.Quantity : 0,
              PointId: this.pointId,
              Page: part.Page,
              Position: part.Position,
              PartName: part.Name,
              Name: part.Name,
              EquipmentId: part.EquipmentId
            };
            this.partsForm = this.fb.group({
              parts: this.fb.array([
                this.fb.group({
                  id: [this.trimPartId(this.part.Id), Validators.required],
                  page: [this.part.Page, Validators.required],
                  position: [this.part.Position, Validators.required],
                  partsName: [this.part.PartName, Validators.required]
                })
              ])
            });
          }
        }
      });
    } else {
      this.partsForm = this.fb.group({
        parts: this.fb.array([this.initPart()])
      });
    }
    this.cdRef.detectChanges();
  }

  initPart() {
    return this.fb.group({
      id: ['', Validators.required],
      page: ['', Validators.required],
      position: ['', Validators.required],
      partsName: ['', Validators.required],
      quantity: [1, [Validators.required, Validators.min(1)]],
      equipmentId: [this.equipmentId]
    });
  }

  private setImportedParts() {
    const groupArray = [];
    this.importedParts.forEach((part) => {
      groupArray.push(
        this.fb.group({
          id: [this.trimPartId(part.parts), Validators.required],
          page: [part.page, Validators.required],
          position: [part.position, Validators.required],
          partsName: [part.name, Validators.required],
          quantity: [part.quantity, Validators.required],
          equipmentId: [part.machine]
        })
      );
    });
    return groupArray;
  }

  get formData() {
    return <UntypedFormArray>this.partsForm.controls['parts'];
  }

  add() {
    const control = <UntypedFormArray>this.partsForm.controls['parts'];
    control.push(this.initPart());
  }

  updateQuantity(quantityControl: UntypedFormControl, quantity: number) {
    if (quantityControl.value > 0 || (quantityControl.value === 0 && quantity > 0)) {
      quantityControl.setValue(quantityControl.value + quantity);
    }
  }

  remove(i: number) {
    const control = <UntypedFormArray>this.partsForm.controls['parts'];
    control.removeAt(i);
  }

  save(model: any) {
    this.processing = true;
    const newPart = { ...this.part };
    newPart.Name = model.parts[0].partsName;
    newPart.Page = model.parts[0].page;
    newPart.Position = model.parts[0].position;
    newPart.Id = this.trimPartId(model.parts[0].id);
    if (this.partId) {
      this.service.updatePart(this.inspectionId, this.pointId, this.pluginId, this.part, newPart).subscribe((data) => {
        this.processing = false;
        this.goToBack();
      });
    } else {
      this.service
        .addParts(
          this.inspectionId,
          this.pointId,
          this.pluginId,
          _.map(model.parts, (el: any) => ({
            Id: this.trimPartId(el.id),
            Quantity: el.quantity,
            Page: el.page,
            Position: el.position,
            Name: el.partsName,
            EquipmentId: el.equipmentId,
            FromPartsView: this.isImport
          })),
          this.isImport
        )
        .subscribe((el) => {
          this.processing = false;
          this.goToBack();
        });
    }
  }

  trimPartId(partId: string): string {
    return partId?.trim()?.toUpperCase();
  }

  // Note 2022 : there is no more homepage for partsview, so menu is not useful anymore
  // Maybe we can later add options here
  // public openMenu(): void {
  //   this.modal.modalFooter = false;
  //   this.modal.modalHeader = false;
  //   this.modal.modalMessage = false;
  //   this.modal.open(PartMenuComponent);
  // }

  public selectMenu(item: any) {
    // switch (item) {
    //   case 1:
    //     this.gotoPartsViewPage();
    //     break;
    //   default:
    // }
  }
}
